import { useMst } from "../../../stores/main";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { reaction, toJS, untracked } from "mobx";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type { MotionColorReport } from "@blings/blings-player/editor-tools";
import type { LayerInspectorSelection } from "../../../helpers/layerInspectorTools";
import { useAccountFontFaces } from "./useAccountFontFaces";
import { useLayerVisualPropertiesCapabilities } from "./useLayerVisualPropertiesCapabilities";
import { useLayerVisualPropertyActions } from "./useLayerVisualPropertyActions";
import type {
  FieldConnectionActions,
  OpenVisualPropertyConnector,
} from "./types";
import { getDocumentAnimationSummary, getDocumentMediaAssets } from "./utils";
import {
  buildPropertyConnectorModData,
  getFallbackConnectorSelections,
} from "./propertyConnectors";
import { modsDefinitions } from "../../mods/mod-definitions/mod-definitions";

type DocumentColorStatus = "idle" | "loading" | "ready" | "error";

const PANEL_FRAME_SAMPLE_MS = 250;

type DocumentColorState = {
  report?: MotionColorReport;
  status: DocumentColorStatus;
};

function useSampledPanelFrame(playerStore: { currentFrame: number }) {
  const latestFrameRef = useRef(untracked(() => playerStore.currentFrame));
  const [sampledFrame, setSampledFrame] = useState(
    () => latestFrameRef.current
  );
  const lastPublishedAtRef = useRef(0);
  const pendingFrameRef = useRef<number | undefined>();
  const timerRef = useRef<number | undefined>();

  const clearPendingTimer = useCallback(() => {
    if (timerRef.current === undefined) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);

  const publishFrame = useCallback(
    (frame: number) => {
      clearPendingTimer();
      pendingFrameRef.current = undefined;
      latestFrameRef.current = frame;
      lastPublishedAtRef.current = Date.now();
      setSampledFrame((current) => (current === frame ? current : frame));
    },
    [clearPendingTimer]
  );

  useEffect(() => {
    const scheduleFrame = (frame: number) => {
      latestFrameRef.current = frame;

      const now = Date.now();
      const elapsed = now - lastPublishedAtRef.current;
      if (!lastPublishedAtRef.current || elapsed >= PANEL_FRAME_SAMPLE_MS) {
        publishFrame(frame);
        return;
      }

      pendingFrameRef.current = frame;
      if (timerRef.current !== undefined) return;

      timerRef.current = window.setTimeout(() => {
        timerRef.current = undefined;
        publishFrame(pendingFrameRef.current ?? latestFrameRef.current);
      }, PANEL_FRAME_SAMPLE_MS - elapsed);
    };

    const dispose = reaction(() => playerStore.currentFrame, scheduleFrame, {
      fireImmediately: true,
    });

    return () => {
      dispose();
      clearPendingTimer();
    };
  }, [clearPendingTimer, playerStore, publishFrame]);

  const getCurrentFrame = useCallback(() => latestFrameRef.current, []);

  return { sampledFrame, getCurrentFrame };
}

export function useLayerVisualPropertiesPanelModel() {
  const {
    platformStore: {
      selectedVideoPartName,
      uploadAssetToProject,
      selectedProjectBasicInfo,
    },
    accountStore,
    dynamicDataStore: { perVideoCurrentData, liveControlCurrentData },
    modsStore: {
      addMod,
      mods,
      trigger: modsRevision,
      setActiveMod,
      setHoveredControlMod,
    },
    playerStore,
  } = useMst();
  const {
    jsonFile,
    mainAssetKey,
    layerInspectorAvailable,
    layerInspectorPrimarySelection,
    layerInspectorSelections,
  } = playerStore;
  const { sampledFrame: currentFrame, getCurrentFrame } = useSampledPanelFrame(
    playerStore
  );
  const lastSingleSelectionRef = useRef<LayerInspectorSelection | undefined>();
  const selectionCount = layerInspectorSelections.length;
  const currentSingleSelection =
    selectionCount === 1 ? layerInspectorPrimarySelection : undefined;

  useEffect(() => {
    if (currentSingleSelection) {
      lastSingleSelectionRef.current = currentSingleSelection;
      return;
    }

    if (layerInspectorAvailable || selectionCount > 1) {
      lastSingleSelectionRef.current = undefined;
    }
  }, [currentSingleSelection, layerInspectorAvailable, selectionCount]);

  const shouldUseLastSelection =
    selectionCount === 0 &&
    !layerInspectorAvailable &&
    Boolean(lastSingleSelectionRef.current);
  const effectivePrimarySelection = shouldUseLastSelection
    ? lastSingleSelectionRef.current
    : layerInspectorPrimarySelection;
  const effectiveSelectionCount = shouldUseLastSelection ? 1 : selectionCount;
  const effectiveSelections = useMemo(
    () =>
      shouldUseLastSelection && effectivePrimarySelection
        ? [effectivePrimarySelection]
        : layerInspectorSelections,
    [
      effectivePrimarySelection,
      layerInspectorSelections,
      shouldUseLastSelection,
    ]
  );

  const modSnapshots = useMemo(() => {
    // MST mutates mod data in place; the trigger invalidates this snapshot.
    void modsRevision;
    return mods.map((mod) => ({
      id: mod.id.toString(),
      moddata: toJS(mod.moddata),
    }));
  }, [mods, modsRevision]);
  const controlMods = useMemo(() => modSnapshots.map((mod) => mod.moddata), [
    modSnapshots,
  ]);
  const fieldConnectionActions = useMemo<FieldConnectionActions>(
    () => ({
      getModIdByIndex: (modIndex) => modSnapshots[modIndex]?.id,
      onOpenMod: (modId) => setActiveMod(modId),
      onHoverMod: (modId) => setHoveredControlMod(modId),
    }),
    [modSnapshots, setActiveMod, setHoveredControlMod]
  );
  const openPropertyConnector = useCallback<OpenVisualPropertyConnector>(
    (fields, options) => {
      const selections = getFallbackConnectorSelections(
        fields,
        effectiveSelections,
        mainAssetKey
      );
      const modData = buildPropertyConnectorModData({
        fields,
        mainAssetKey,
        componentIndex: options?.componentIndex,
        preferredType: options?.preferredType,
        selectedColor: options?.selectedColor,
      });
      const baseModData = modData.type && modsDefinitions[modData.type]?.change;

      if (options?.preferredType === "media" && !selections.length) {
        return;
      }

      addMod({ ...(baseModData ?? {}), ...modData }, undefined, selections);
    },
    [addMod, effectiveSelections, mainAssetKey]
  );
  // Read the observable here so the observer re-renders once the account load
  // fills the store; the MST node identity keys the plain-object snapshot.
  const accountFontsNode = accountStore.getFontsForAccount(
    selectedProjectBasicInfo?.projectAccountId
  );
  const accountFonts = useMemo(() => toJS(accountFontsNode), [
    accountFontsNode,
  ]);
  useAccountFontFaces(accountFonts);
  const capabilitiesModel = useLayerVisualPropertiesCapabilities({
    jsonFile,
    currentFrame,
    mainAssetKey,
    layerInspectorPrimarySelection: effectivePrimarySelection,
    layerInspectorSelections: effectiveSelections,
    mods: controlMods,
    data: perVideoCurrentData,
    liveControlData: liveControlCurrentData,
    accountFonts,
  });
  const layerActionTarget = layerInspectorAvailable
    ? capabilitiesModel.target
    : null;
  const actions = useLayerVisualPropertyActions({
    getCurrentFrame,
    target: layerActionTarget,
    targets: capabilitiesModel.targets,
  });
  const documentSummary = useMemo(() => getDocumentAnimationSummary(jsonFile), [
    jsonFile,
  ]);
  const documentMediaAssets = useMemo(() => getDocumentMediaAssets(jsonFile), [
    jsonFile,
  ]);
  const shouldInspectDocument =
    effectiveSelectionCount === 0 && Boolean(jsonFile);
  const [documentColorState, setDocumentColorState] = useState<
    DocumentColorState
  >({ status: "idle" });

  useEffect(() => {
    let isActive = true;

    if (!shouldInspectDocument || !jsonFile) {
      setDocumentColorState({ status: "idle" });
      return;
    }

    setDocumentColorState({ status: "loading" });

    try {
      motionAPI.inspect
        .getDocumentColors(jsonFile as any)
        .then((report) => {
          if (isActive) {
            setDocumentColorState({ report, status: "ready" });
          }
        })
        .catch(() => {
          if (isActive) {
            setDocumentColorState({ status: "error" });
          }
        });
    } catch {
      setDocumentColorState({ status: "error" });
    }

    return () => {
      isActive = false;
    };
  }, [jsonFile, shouldInspectDocument]);

  return {
    ...capabilitiesModel,
    ...actions,
    selectionCount: effectiveSelectionCount,
    isLayerInspectorAvailable: layerInspectorAvailable,
    isUsingCachedLayerSelection: shouldUseLastSelection,
    sceneName: selectedVideoPartName ?? undefined,
    uploadAssetToProject,
    fieldConnectionActions,
    openPropertyConnector,
    documentSummary,
    documentColorReport: documentColorState.report,
    documentColorStatus: documentColorState.status,
    documentMediaAssets,
  };
}
